import React, { useCallback, useState } from 'react';
import dayGridPlugin from '@fullcalendar/daygrid';
import FullCalendar from '@fullcalendar/react';
import {
	Box,
	Typography,
	Paper,
	List,
	ListItem,
	ListItemText,
	ListItemButton,
	Checkbox,
	Button,
} from '@mui/material';
import esLocale from '@fullcalendar/core/locales/es';
import interactionPlugin from '@fullcalendar/interaction';
import { EventModal } from './EventModal';
import { DateModal } from './DateModal';
import { AREAS_PRIORITY, AREA_COLORS } from '../utils/lists';
import { isAdmin } from '../../helpers/utils';

const listItemButtonStyle = {
	'&:hover': {
		backgroundColor: '#f5f5f5',
	},
	height: '50px',
};

export const FullCalendarComponent = ({
	data = [],
	setPriorityOrder,
	responseData,
	priorityOrder,
	user = null,
}) => {
	// Modal
	const [openModal, setOpenModal] = useState(false);
	const [openDateModal, setOpenDateModal] = useState(false);

	// Is user admin?
	const isUserAdmin = isAdmin(user);

	// Is user from Management ("Gerencia")?
	const isUserFromManagement = user?.area?.name === 'Logística';

	const totalAccess = isUserAdmin || isUserFromManagement;

	// Event data
	const [eventData, setEventData] = useState(null);
	const [dateData, setDateData] = useState(null);

	// Areas' filter
	const [showPriorityOrder, setShowPriorityOrder] = useState(totalAccess);

	// Priority list
	const [allSelected, setAllSelected] = useState(true);

	// Handle event click for an event
	const handleEventClick = useCallback((info) => {
		const currentEvent = info.event;
		setEventData(currentEvent);
		setOpenModal(true);
	}, []);

	// Handle event click for a date
	const handleDateClick = useCallback(
		(info) => {
			const clickedDate = info.dateStr;
			let eventsOnClickedDate = data.filter(
				(event) => event.start === clickedDate
			);

			if (eventsOnClickedDate?.length > 0) {
				let finalEvents = [];

				// Order keys
				const orderKeys = eventsOnClickedDate.map(
					(x) => x.title.split('-')[0]
				);
				// All order objects
				let orders = Object.entries(responseData).filter(([key]) =>
					orderKeys.includes(key)
				);

				orders.forEach((x) => {
					const numberOrder = x[0];
					let currentBias = eventsOnClickedDate.find((y) =>
						y.title.includes(numberOrder)
					);

					if (currentBias) {
						const currentBiasAreas =
							currentBias.extendedProps.bias.areas;
						let eventsCurrentBias = Object.entries(
							currentBiasAreas
						).filter(
							([_, areaObj]) => areaObj.date === clickedDate
						);

						eventsCurrentBias = eventsCurrentBias.forEach(
							(item) => {
								if (
									priorityOrder.find(
										(poa) => poa.name === item[1].name
									).showing // poa = priority order area
								) {
									finalEvents.push({
										...currentBias,
										color: item[1].color,
									});
								}
							}
						);
					}
				});

				setDateData({
					date: clickedDate,
					events: finalEvents,
				});

				setOpenDateModal(true);
			}
		},
		[data]
	);

	const handleCloseModal = () => setOpenModal(false);

	const handleCloseDateModal = () => setOpenDateModal(false);

	const handleSelectAreas = () => {
		// Define new state
		const newStateFlag = !allSelected;

		// Update selection flag
		setAllSelected(newStateFlag);

		// Select/Diselect all the areas by setting the original list
		setPriorityOrder(
			AREAS_PRIORITY.map((area) => ({ ...area, showing: newStateFlag }))
		);
	};

	const ColorDotArea = ({ areaName }) => (
		<span
			style={{
				color: isUserAdmin
					? AREA_COLORS.find((x) => x.area === areaName).color
					: '#2196F3',
				fontSize: '4em',
				margin: '0 10px 10px 0',
			}}
		>
			•
		</span>
	);

	const handleChangeCheckbox = (list, priority) => {
		// Set select flag
		setAllSelected(false);
		// Get index of current priority
		const currentIndex = list.findIndex((x) => x.priority === priority);
		// Create a priority copy
		let newPriorities = [...list];
		// Change value to its inverse
		newPriorities[currentIndex].showing =
			!newPriorities[currentIndex].showing;
		// Update priority order
		setPriorityOrder(newPriorities);
	};

	const PriorityOrder = ({ list }) => {
		return (
			<Paper elevation={3} style={{ padding: '20px', margin: '16px' }}>
				<center>
					<Typography variant='h6' gutterBottom>
						Áreas
					</Typography>
					{/* <br /> */}
					<Button
						size='small'
						variant='text'
						onClick={handleSelectAreas}
					>
						{allSelected ? 'Deseleccionar' : 'Seleccionar'} todo
					</Button>
				</center>
				<List>
					{list.map(({ name, priority, showing }) => (
						<ListItem key={name} disablePadding>
							<ListItemButton sx={listItemButtonStyle}>
								<ColorDotArea areaName={name} />
								<Checkbox
									edge='start'
									checked={showing}
									onChange={() =>
										handleChangeCheckbox(
											list,
											priority,
											showing
										)
									}
								/>
								<ListItemText
									primary={`${priority}: ${name}`}
								/>
							</ListItemButton>
						</ListItem>
					))}
				</List>
			</Paper>
		);
	};

	const CalendarComponent = ({
		events,
		totalAccess,
		showPriorityOrder,
		setShowPriorityOrder,
		handleEventClick,
		handleDateClick,
	}) => {
		return (
			<Box
				sx={{
					...boxStyle,
					width: totalAccess && showPriorityOrder ? '65%' : '95%',
				}}
			>
				<Paper elevation={3} style={paperStyle}>
					<FullCalendar
						plugins={[dayGridPlugin, interactionPlugin]}
						initialView='dayGridMonth'
						events={events}
						headerToolbar={{
							left: 'today prev,next',
							center: 'title',
							right: 'priorityColumn',
						}}
						customButtons={{
							priorityColumn: {
								text: totalAccess
									? `${
											showPriorityOrder
												? 'Ocultar'
												: 'Mostrar'
									  } filtro`
									: '-',
								click: () => {
									if (totalAccess) {
										setShowPriorityOrder(
											!showPriorityOrder
										);
									}
								},
							},
						}}
						hiddenDays={[0]} // Hide Sundays
						initialDate={events[0]?.start}
						locales={[esLocale]}
						locale='es'
						eventClick={handleEventClick}
						dateClick={handleDateClick}
						editable
					/>
				</Paper>
			</Box>
		);
	};

	return (
		<div style={divStyle}>
			<>
				{/* Event Modal */}
				<EventModal
					eventData={eventData}
					openModal={openModal}
					handleCloseModal={handleCloseModal}
				/>

				{/* Date Modal */}
				<DateModal
					dateData={dateData}
					openModal={openDateModal}
					handleCloseModal={handleCloseDateModal}
				/>

				{/* Calendar */}
				<CalendarComponent
					events={data}
					totalAccess={totalAccess}
					showPriorityOrder={showPriorityOrder}
					setShowPriorityOrder={setShowPriorityOrder}
					handleEventClick={handleEventClick}
					handleDateClick={handleDateClick}
				/>

				{/* Priority order */}
				{showPriorityOrder && <PriorityOrder list={priorityOrder} />}
			</>
		</div>
	);
};

const boxStyle = {
	display: 'flex',
	width: '65%',
	flexWrap: 'wrap',
	'& > :not(style)': {
		m: 2,
		p: 2,
		width: '100%',
		height: '250px',
	},
};

const paperStyle = {
	overflowY: 'scroll',
	height: '95%',
};

const divStyle = {
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-evenly',
	height: '90%',
};
